import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import {
  deDE as muiLocaleGerman,
  enUS as muiLocaleEnglish,
} from '@mui/material/locale';
import {
  deDE as muiDataGridLocaleGerman,
  enUS as muiDataGridLocaleEnglish,
} from '@mui/x-data-grid/locales';

import { colorPalettes } from 'src/theme/colors';
import getColorAliases from 'src/theme/colors/aliases';
import { basicColors } from 'src/theme/colors/basic';
import baseTheme, { defaultTheme } from 'src/theme/themes';

// Disable css vendor prefixes for development.
// https://github.com/emotion-js/emotion/issues/2212#issuecomment-760988427
// Set "compat" to "true" to avoid "pseudo class ':first-child' is potentially unsafe" warnings.
// https://github.com/mui/material-ui/issues/24894#issuecomment-778617625
const cacheOptions = {
  key: 'css',
  prepend: true,
  ...(import.meta.env.NODE_ENV === 'development' && { stylisPlugins: [] }),
};
const cssCache = createCache(cacheOptions);
cssCache.compat = true;

export interface LocalizedThemeProviderProps {
  children?: ReactNode;
}

const LocalizedThemeProvider: FC<LocalizedThemeProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const locales: Record<string, any> = {
    de: { ...muiLocaleGerman, ...muiDataGridLocaleGerman },
    en: { ...muiLocaleEnglish, ...muiDataGridLocaleEnglish },
  };

  const localizedTheme = useMemo(() => {
    const locale = locales[i18n.language] || locales.en;
    const customizedColor = {
      ...colorPalettes,
      ...basicColors,
      // Colors from the backend color config contain color palettes and color aliases
      ...(window.colorConfig ? window.colorConfig : {}),
    };
    // Generate color aliases with customized color palettes
    const colorAliases = getColorAliases(customizedColor);
    // Overwrite color aliases with colors aliases from customized colors
    const allColors = { ...colorAliases, ...customizedColor };

    const customTheme = createTheme(defaultTheme(allColors), baseTheme);
    return createTheme(customTheme, { ...locale });
  }, [i18n.language]);

  return (
    <CacheProvider value={cssCache}>
      <ThemeProvider theme={localizedTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
};

export default LocalizedThemeProvider;
